import Consts from "Interfaces/Consts";
import CarouselItem, { CarouselApiItem, FieldMapping, UmbracoApiListKeys } from './Carousel.interface';
import { LinkItem, MediaItem } from "Interfaces/general.interface";

const GOV_IMAGE_SRC = "/Images/Components/gov-logo.jpg";
const DATETIME_FIELD_NAME = 'LastEndParticipationTime';

const useApiList = () => {

    const handleDataFromApi = async (umbracoList: UmbracoApiListKeys[]) => {
        try {

            const list = umbracoList[0];

            const apiUrl = list.apiUrl;

            const apiData = await fetchDataFromApi(apiUrl);

            const apiItems = extractItemsFromApiData(apiData, list.apiItemsPath);

            const mappedItems: CarouselApiItem[] = apiItems
                .map((item, idx: number) => convertApiItemToCarouselItem(item, list, idx))
                .filter(checkItemValidToDisplay);

            if (mappedItems.length === 0) return Consts.noResultsAtTheTime;
            
            return mappedItems;

        }
        catch (err) {
            // console.log("Error api map: ", err);
            return Consts.apiFetchErrorMsg;
        }
    }

    const checkItemValidToDisplay = (item: CarouselApiItem) => (item.content || item.title);

    const convertApiItemToCarouselItem = (item: any, list: UmbracoApiListKeys, idx: number) => {

        const mappedItem: CarouselApiItem = {
            index: (idx + 1).toString(),
            title: item[list.titleFieldMapping] || '',
            content: item[list.contentFieldMapping] || '',
            createDate: null,
            mobileImage: null,
            richDescription: "",
            url: "",
            icon: null,
            image: mapImageItem(item, list),
            contentRows: mapItemContentRows(item, list.apiFieldMappings),
            link: mapLinkItem(item, list),
        }

        return mappedItem;
    }

    const mapLinkItem = (item: any, list: UmbracoApiListKeys): LinkItem => {
        const link: LinkItem = {
            url: item[list.linkFieldMapping] || "",
            name: list.buttonTitle || "",
            title: list.buttonTitle || "",
            target: "_blank"
        };
        return link;
    }

    const mapItemContentRows = (item: any, apiFieldMappings: FieldMapping[]) => {
        const contentRows = apiFieldMappings.map(cr => {
            return {
                icon: cr.apiFieldIcon || "",
                title: cr.apiFieldTitle || "",
                content: convertDateStringToNormalDate(cr.apiFieldName, item[cr.apiFieldName])
            }
        });
        return contentRows;
    }

    const imageBuilder = (imageUrl: string) => {
        const image: MediaItem = {
            alt: "",
            isImage: true,
            squareUrl: imageUrl,
            url: imageUrl,
            rectangleUrl: imageUrl,
            copyRight:""
        }
        return image
    }

    const mapImageItem = (item: any, list: UmbracoApiListKeys): MediaItem => {

        // Priority of images: 1. Image From Api 2. List's DefaultImage 3. GOV_IMAGE_SRC
        const hasImageInApi = list.imageFieldMapping && item[list.imageFieldMapping];
        const hasListDefaultImg = list.defaultImage && list.defaultImage.url;

        if (hasImageInApi) {
            const apiImgUrl = item[list.imageFieldMapping] || '';
            const apiImg = imageBuilder(apiImgUrl);
            return apiImg;
        };

        if (hasListDefaultImg) return list.defaultImage;

        const defaultImg = imageBuilder(GOV_IMAGE_SRC);

        return defaultImg;

    }

    const fetchDataFromApi = async (apiUrl: string) => fetch(apiUrl).then(res => res.json());

    const extractItemsFromApiData = (apiData, pathToItems: string): any[] => {

        if (!apiData) throw Error("ERROR: No Data response from API...");

        if (Array.isArray(apiData)) { return apiData; }

        if (!pathToItems) throw Error("Missing property: 'pathToItems'");

        const path = pathToItems.split('/').filter(i => i);
        let items = apiData;

        for (let i = 0; i < path.length; i++) {
            const directory = items[path[i]];

            const isDirArray = Array.isArray(directory) && i < path.length - 1;
            if (!directory || isDirArray) {
                throw Error(`Error in 'extractItemsFromApiData': Path Property '${path[i]}' is either missing or invalid`)
            };
            items = directory;
        }

        return items;
    }

    const checkIfListIsApiMappingOrNormal = (list: CarouselItem[] | UmbracoApiListKeys[]) => {

        const API_URL_KEY: keyof UmbracoApiListKeys = 'apiUrl';

        if (list.length === 1 && list[0][API_URL_KEY]) return true;

        return false;

    }

    const convertDateStringToNormalDate = (fieldName: string, value: string) => {

        if (fieldName !== DATETIME_FIELD_NAME || typeof value !== 'string') return value;

        const dateAndTime = value.split('T');
        const date = dateAndTime[0].split('-').reverse().join('.');
        const time = dateAndTime[1].slice(0, 5);

        return `${time} ${date}`;
    }

    const checkIfUrlIsSupported = (umbracoList: UmbracoApiListKeys[]) => {
        // supported DOMAINS: api.shitufy.net  / .gov.il 
        const url = umbracoList[0].apiUrl;
        const regex = /(\.gov\.il|api\.shitufi\.net)/i;
        return regex.test(url);
    }

    return {
        checkIfListIsApiMappingOrNormal,
        handleDataFromApi,
        checkIfUrlIsSupported
    }

}

export default useApiList;